import * as React from 'react'

import Head from 'next/head'
import { useAmp } from 'next/amp'
import getConfig from 'next/config'
import { GetServerSidePropsContext } from 'next'

import { PageCacheConfiguration } from '@thg-commerce/enterprise-core/src/cache/configuration'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { ContentSlug as ContentSlugResolver } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/ContentSlug'
import { Seo as SeoResolver } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/Seo'
import { clientConfig as clientConfigFactory } from '@thg-commerce/enterprise-network/src/ApolloProvider/utils'

import {
  withCacheConfiguration,
  loggerProvider,
} from '@thg-commerce/enterprise-core'
import { ContentHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { EnterpriseNextPage } from '@thg-commerce/enterprise-core/src/App/types'
import {
  ContentSlug,
  ContentSlugType,
  NetworkClient,
  RootQueryContentSlugArgs,
  SeoConfig,
} from '@thg-commerce/enterprise-network'

import CustomError from '../_error'
import { ArticleData, ArticlePage, CategoryPage } from '../../src/components'
import { Post, Category, Tag, PostAuthor as User } from '../../src/types'
import { postValidator } from '../../src/validator'
import { getInfoFromQueryParam, getPaginationVaribles } from '../../src/utils'
import { ContentSlug as CONTENT_SLUG_QUERY } from '../../src/graphql/ContentSlug.graphql'

export const POSTS_PER_PAGE = 15

type SlugPageData = ArticleData | Category | Tag | User | null
export interface SlugPageProps {
  type: ContentSlugType | null
  data: SlugPageData
  page?: number
  hasWebP: boolean
}

const Slug: EnterpriseNextPage<SlugPageProps> = (props) => {
  const { type, data, page } = props
  const isAmp = useAmp()

  if (!type || !data) {
    return (
      <React.Fragment>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <CustomError statusCode={404} />
      </React.Fragment>
    )
  }

  if (type === ContentSlugType.Post) {
    const { post, content, seoConfig } = data as ArticleData
    return (
      <ArticlePage
        isAmp={isAmp}
        hasWebP={props.hasWebP}
        post={post}
        content={content}
        seoConfig={seoConfig}
      />
    )
  }
  if (Object.values(ContentSlugType).includes(type) && page) {
    return (
      <CategoryPage
        isAmp={isAmp}
        category={data as Category | Tag}
        page={page}
        hasWebP={props.hasWebP}
      />
    )
  }

  return (
    <React.Fragment>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <CustomError statusCode={404} />
    </React.Fragment>
  )
}

export const getServerSideProps = withCacheConfiguration<
  { props: SlugPageProps },
  GetServerSidePropsContext
>(
  async (
    context: GetServerSidePropsContext,
  ): Promise<{ props: SlugPageProps; cache?: PageCacheConfiguration }> => {
    const nextConfig = getConfig() as NextConfig
    const request = context.req as EnterpriseRequest
    const config = await loadConfiguration(nextConfig, request)
    const { publicRuntimeConfig } = config
    const { brand, subsite, originUrl } = publicRuntimeConfig.siteDefinition
    const logger = loggerProvider({ brand, subsite, originUrl })
    const apollo = createApolloWithRequest(request, config, true)
    const allowOverrides =
      request.headers['x-enterprise-allow-overrides'] === '1'
    const allowSitePropertyOverrides =
      request.headers['x-enterprise-allow-site-property-overrides'] === '1'
    const clientConfig = clientConfigFactory(
      publicRuntimeConfig,
      allowOverrides,
      allowSitePropertyOverrides,
    )

    apollo.setResolvers({
      Query: {
        contentSlug: ContentSlugResolver(apollo, clientConfig),
        seo: SeoResolver(apollo, clientConfig),
      },
    })

    if (context) {
      const { query, res } = context

      if (query.slug) {
        const { slug: slugToQuery, page } = getInfoFromQueryParam(query.slug)

        const categoryLimit = 0 // in tesseract 0 means no limit, currently we display all child categories regardless of amount
        const categoryOffset = 0

        const { offset: postOffset, limit: postLimit } = getPaginationVaribles(
          page || 1,
          POSTS_PER_PAGE,
        )

        const { data } = await apollo.query<
          { contentSlug: ContentSlug; seo: SeoConfig },
          RootQueryContentSlugArgs & {
            categoryLimit: number
            categoryOffset: number
            postLimit: number
            postOffset: number
          }
        >({
          query: CONTENT_SLUG_QUERY,
          variables: {
            categoryLimit,
            categoryOffset,
            postLimit,
            postOffset,
            path:
              query.slug[0] === 'tag'
                ? `${query.slug[0]}/${slugToQuery}`
                : slugToQuery,
          },
          context: { clientName: NetworkClient.Content },
        })

        if (!data || !data?.contentSlug?.type || !data?.contentSlug?.data) {
          if (res) res.statusCode = 404
          return {
            props: {
              type: null,
              data: null,
              hasWebP: false,
            },
          }
        }

        if (data.contentSlug.type === ContentSlugType.Post) {
          // This validation is temporary until work is complete on REBUILD-876, to prevent the need for complicated code when rendering
          const post =
            data?.contentSlug.data &&
            postValidator.isValidSync(data.contentSlug.data)
              ? (data.contentSlug.data as Post)
              : null
          const seoConfig = data?.seo ? data.seo : null

          if (!post) {
            if (res) res.statusCode = 404
            return { props: { type: null, data: null, hasWebP: false } }
          }

          try {
            const content = JSON.parse(post.content)
            const ajv = new (require('ajv').default)()
            const validate = ajv.compile(
              require(`${__dirname}/../../post-content.schema.json`),
            )

            if (!validate(content)) {
              if (!getConfig().publicRuntimeConfig.IS_PRODUCTION) {
                console.error(
                  'Article content validation failed with errors:',
                  validate.errors,
                )
              }
              throw Error('Failed to validate article content')
            }

            return {
              props: {
                type: data.contentSlug.type,
                data: { post, content, seoConfig },
                hasWebP:
                  (content?.req?.headers?.hasOwnProperty('accept') &&
                    content?.req?.headers?.accept?.includes('image/webp')) ||
                  false,
              },
            }
          } catch (e) {
            const error = e as Error
            if (res) res.statusCode = 404
            logger.error(
              `An error occured whilst validation article (${query.slug}): ${error.message}`,
            )
          }
        }

        if (data.contentSlug.type === ContentSlugType.User) {
          const userData: User = data.contentSlug.data as User
          userData.name = userData.username

          return {
            props: {
              type: data.contentSlug.type,
              page: page || 1,
              data: userData,
              hasWebP:
                (context?.req?.headers?.hasOwnProperty('accept') &&
                  context?.req?.headers?.accept?.includes('image/webp')) ||
                false,
            },
          }
        }
        if (
          data.contentSlug.type !== ContentSlugType.Post &&
          Object.values(ContentSlugType).includes(data.contentSlug.type)
        ) {
          return {
            props: {
              type: data.contentSlug.type,
              page: page || 1,
              data: data.contentSlug.data as Category | Tag | User,
              hasWebP:
                (context?.req?.headers?.hasOwnProperty('accept') &&
                  context?.req?.headers?.accept?.includes('image/webp')) ||
                false,
            },
            cache: {
              ttl: 300,
              grace: 3600,
            },
          }
        }
      }
    }
    return { props: { type: null, data: null, hasWebP: false } }
  },
)

Slug.Layout = ContentHeaderLayout

Slug.useAmpLayout = true

export default Slug
